import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import LabsSection from './components/LabsSection';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../auth/AuthProvider';

const PAGE_SIZE = 10;

const LabsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page') || '1'));
  const offset = useMemo(() => (page - 1) * PAGE_SIZE, [page]);

  const [items, setItems] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('ch_labs_posts')
        .select('id, title, author_name, created_at, replies:ch_labs_post_replies(id)')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE);

      if (error) {
        setItems([]);
        setHasNext(false);
        return;
      }

      const rows = data || [];
      setHasNext(rows.length > PAGE_SIZE);
      const pageRows = rows.slice(0, PAGE_SIZE);

      setItems(
        pageRows.map((n) => ({
          id: n.id,
          title: n.title,
          authorName: n.author_name || '익명',
          date: n.created_at ? String(n.created_at).slice(0, 10) : '',
          hasReply: Array.isArray(n.replies) && n.replies.length > 0,
        }))
      );
    } finally {
      setIsLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <Seo title="꿈현 Labs - 아이디어 제안 & 혁신 플랫폼" canonicalPath="/labs" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-5 md:py-8 lg:py-12">
        <div className="flex items-start justify-between gap-3 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">꿈현 Labs에게 요구합니다!</h1>
            <p className="text-sm text-muted-foreground mt-1">유저들의 의견을 모아 개선합니다.</p>
          </div>

          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => navigate('/labs-editor')}
          >
            글 등록
          </Button>
        </div>

        {isLoading ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6">
            <p className="text-sm text-muted-foreground caption">로딩 중...</p>
          </div>
        ) : (
          <LabsSection items={items} hideHeader />
        )}

        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={() => setSearchParams({ page: String(Math.max(1, page - 1)) })}
            disabled={page <= 1 || isLoading}
            className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
          >
            이전
          </button>

          <div className="text-sm text-muted-foreground caption">페이지 {page}</div>

          <button
            type="button"
            onClick={() => setSearchParams({ page: String(page + 1) })}
            disabled={!hasNext || isLoading}
            className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
          >
            다음
          </button>
        </div>

        {!user ? (
          <div className="mt-6 text-xs text-muted-foreground caption">
            글 등록/수정/삭제는 로그인 후 이용할 수 있습니다.
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default LabsPage;
